import { getChainId, isNativeAddress } from '@spritz-finance/sdk'
import { BigNumber, Contract } from 'ethers'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RPC_PROVIDERS } from '../../../../../web3/connection/providers'
import { ERC20Interface } from '../../../../../web3/hooks/interfaces'
import { TokenDetails } from './BalanceProvider'

export const useTokenBalance = (token: TokenDetails) => {
    const [balance, setBalance] = useState<BigNumber | undefined>(undefined)
    const prevBalanceRef = useRef<BigNumber | undefined>(undefined)

    const validErc20Token = !!token.address && !isNativeAddress(token.address)

    const provider = useMemo(() => {
        return RPC_PROVIDERS[getChainId(token.network)] ?? null
    }, [token.network])

    const tokenContract = useMemo(
        () =>
            validErc20Token && provider
                ? new Contract(token.address, ERC20Interface, provider)
                : null,
        [provider, token.address, validErc20Token]
    )

    const fetchBalance = useCallback(async () => {
        if (!tokenContract || !token.wallet) return

        const rawBalance = await tokenContract.balanceOf(token.wallet)

        // Only change balance state if it differs from previous one
        if (rawBalance !== prevBalanceRef.current) {
            prevBalanceRef.current = rawBalance
            setBalance(rawBalance)
        }
    }, [token.wallet, tokenContract])

    useEffect(() => {
        if (!tokenContract) return
        fetchBalance()
    }, [fetchBalance, tokenContract])

    useEffect(() => {
        if (!tokenContract) return

        provider?.on('block', fetchBalance)

        return () => {
            provider?.off('block', fetchBalance)
        }
    }, [fetchBalance, provider, tokenContract])

    return { balance }
}
